// VENDOR-LIFECYCLE.2 — Lifecycle History section for VendorDetailPage Overview tab
// D-VENDOR-LIFECYCLE.2-PE-3: lifecycle events from GET /vendors/:id/lifecycle-events
// Shown for all vendors with lifecycle history (active reactivated vendors + offboarded)

import { format } from 'date-fns';
import { History } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useVendorLifecycleEvents } from '@/api/hooks/useVendors';

const EVENT_LABEL: Record<string, string> = {
  'vendor.retired': 'Retired',
  'vendor.reactivated': 'Reactivated',
};

interface VendorLifecycleHistoryProps {
  vendorId: string;
}

export function VendorLifecycleHistory({ vendorId }: VendorLifecycleHistoryProps) {
  const { data: events = [], isLoading } = useVendorLifecycleEvents(vendorId);

  if (isLoading) {
    return (
      <div className="pt-4 border-t border-border-subtle space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (events.length === 0) return null;

  return (
    <div className="pt-4 border-t border-border-subtle">
      <div className="flex items-center gap-2 mb-3">
        <History className="h-4 w-4 text-text-secondary" />
        <p className="text-sm font-semibold text-text-primary">Lifecycle History</p>
      </div>

      <div className="space-y-2">
        {events.map((event, idx) => {
          const reason = typeof event.metadata?.reason === 'string' ? event.metadata.reason : null;
          const formattedDate = event.occurred_at
            ? format(new Date(event.occurred_at), 'MMM d, yyyy')
            : '—';

          return (
            <div
              key={idx}
              className="rounded-lg border border-border-subtle px-3 py-2.5 space-y-1"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-text-primary">
                  {EVENT_LABEL[event.event_type] ?? event.event_type}
                </span>
                <span className="text-xs text-text-secondary shrink-0">{formattedDate}</span>
              </div>
              {reason && (
                <p className="text-xs text-text-secondary leading-relaxed">{reason}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
